import React, { useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, HeightProps, SizeProps, WidthProps } from 'styled-system';
import { backgroundColor, borderRadius, size, themeGet } from 'styled-system';

import { useWishlist, useWishlistState } from '../../hooks/wishlist';
import { useLocalization } from '../../providers/LocaleProvider';
import { useWishlistContext } from '../../providers/WishlistProvider';
import { pulse } from '../../theme/keyframes';
import type { BackgroundColorProps } from '../../theme/system-types';
import type { CursorProps } from '../../theme/system-utilities';
import { cursor, themeHeight, themeWidth } from '../../theme/system-utilities';
import type { ArticleTracking } from '../../types/Article';
import { IconButton } from '../Button/Icon';
import type { BaseIconProps } from '../Icon';
import Icon from '../Icon';
import { dispatchWishlistEvent } from './tracking';
import { useWishlistModal } from './useWishlistModal';
import WishlistErrorModal from './WishlistErrorModal';

interface IsWishlistedProps {
  isWishlisted: boolean;
}

type WishlistIconProps = BaseIconProps & SizeProps & HeightProps & WidthProps;

interface StyledWishlistIconProps extends WishlistIconProps, IsWishlistedProps {
  name: string;
}

interface WishlistButtonProps {
  sku: string;
  productName: string;
  index?: number;
  tracking?: ArticleTracking;
  className?: string;
  id?: string;
  iconProps?: WishlistIconProps;
}

const enum IconColor {
  Inactive = 'colors.sys.neutral.icon.default',
  Hover = 'colors.sys.primary.border.hover',
  Active = 'colors.sys.primary.text.active',
  Wishlisted = 'colors.sys.primary.icon.active',
}

const ICON_SIZE_S = 24;
const ICON_SIZE_XL = 32;
const ICON_SIZE = [ICON_SIZE_S, null, null, ICON_SIZE_XL];

const Button = styled(IconButton)<
  SizeProps & BorderRadiusProps & CursorProps & BackgroundColorProps
>`
  ${borderRadius};
  ${cursor};
  ${size};
  ${backgroundColor};

  &:focus-visible {
    outline-offset: -6px;
  }

  &:hover > svg {
    fill: ${themeGet(IconColor.Hover)};
  }

  &:active > svg {
    fill: ${themeGet(IconColor.Active)};
  }

  &[aria-checked='true']:hover > svg {
    fill: ${themeGet(IconColor.Hover)};
  }
`;

const iconAnimationCSS = ({ isWishlisted }: IsWishlistedProps) =>
  isWishlisted &&
  css`
    animation: ${pulse} 1s ease;
  `;

const StyledWishlistIcon = styled(Icon)<StyledWishlistIconProps>`
  ${size};
  ${themeWidth};
  ${themeHeight};
  ${iconAnimationCSS};
  fill: ${({ isWishlisted }: IsWishlistedProps) =>
    isWishlisted ? themeGet(IconColor.Wishlisted) : themeGet(IconColor.Inactive)};
`;

const WishListButton = ({
  sku,
  tracking,
  productName,
  index: position,
  iconProps,
  ...props
}: WishlistButtonProps) => {
  const { r } = useLocalization();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wishlistContext = useWishlistContext();
  const [isWishlisted, setIsWishlisted] = useWishlistState(sku, wishlistContext?.state);
  const { isModalOpen, errorCode, onModalClose, onError } = useWishlistModal();
  const { isInProgress, clickHandler } = useWishlist({
    sku,
    isWishlisted,
    setIsWishlisted,
    optimistic: true,
    onError,
    onSuccess: (wishlistState: boolean) => {
      dispatchWishlistEvent({ wishlistState, tracking, position });
    },
    onFinished: () => {
      buttonRef.current?.focus();
    },
  });

  return (
    <>
      <Button
        ref={buttonRef}
        data-testid="wishlist-button"
        aria-label={
          isWishlisted
            ? r.t('h24_remove_product_from_wishlist', { productName })
            : r.t('h24_add_product_to_wishlist', { productName })
        }
        role="checkbox"
        aria-disabled={isInProgress}
        aria-checked={isWishlisted}
        cursor="pointer"
        borderRadius="circle"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (isInProgress) {
            return;
          }
          clickHandler(e);
        }}
        {...props}
      >
        <StyledWishlistIcon
          name={isWishlisted ? 'wishlistFilled' : 'wishlist'}
          dataTestId={isWishlisted ? 'wishlistFilled' : 'wishlist'}
          isWishlisted={isWishlisted}
          size={ICON_SIZE}
          {...iconProps}
        />
      </Button>
      {isModalOpen && (
        <WishlistErrorModal
          isModalOpen={isModalOpen}
          errorCode={errorCode}
          onClose={onModalClose}
        />
      )}
    </>
  );
};

export default WishListButton;
